import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';
import MessageFormatter from '../MessageFormatter';

const DashboardTab = ({ onSwitchToChildren }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [showConversationsModal, setShowConversationsModal] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [childConversations, setChildConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [embeddedChat, setEmbeddedChat] = useState(null);
  const [embeddedMessages, setEmbeddedMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [interestSuggestions, setInterestSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedInterest, setSelectedInterest] = useState(null);
  const [selectedChildForInterest, setSelectedChildForInterest] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoadingDashboard(true);
      const response = await api.get('/parent/dashboard');
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      handleApiError(err);
    } finally {
      setLoadingDashboard(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const loadChildConversations = async (childId, childUsername) => {
    try {
      setLoadingConversations(true);
      setSelectedChild(childUsername);
      setSelectedChildId(childId);
      const response = await api.get(`/parent/children/${childId}/conversations`);
      setChildConversations(response.data);
      setShowConversationsModal(true);
    } catch (err) {
      console.error('Error loading conversations:', err);
      handleApiError(err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleConversationClick = async (conversation, childId) => {
    try {
      setLoadingMessages(true);
      setEmbeddedChat({ conversation, childId });
      const response = await api.get(`/parent/children/${childId}/conversations/${conversation.id}/messages`);
      setEmbeddedMessages(response.data);
    } catch (err) {
      console.error('Error loading messages:', err);
      handleApiError(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const closeEmbeddedChat = () => {
    setEmbeddedChat(null);
    setEmbeddedMessages([]);
  };

  const handleInterestClick = async (interest, child) => {
    setSelectedInterest(interest);
    setSelectedChildForInterest(child);
    setInterestModalOpen(true);
    setLoadingSuggestions(true);
    setInterestSuggestions(null);

    try {
      const response = await api.get(
        `/parent/children/${child.id}/interests/${encodeURIComponent(interest)}/suggestions`
      );
      setInterestSuggestions(response.data.suggestions);
    } catch (err) {
      console.error('Error loading suggestions:', err);
      handleApiError(err, 'Unable to load suggestions.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  return (
    <div className="dashboard-tab">
      {loadingDashboard ? (
        <div className="loading">Loading dashboard...</div>
      ) : (
        <>
          <div className="dashboard-stats" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div className="stat-card" style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div className="stat-value" style={{ fontSize: '2.5rem', fontWeight: 700, color: '#667eea', marginBottom: '0.5rem' }}>
                {dashboardData?.total_children || 0}
              </div>
              <div className="stat-label" style={{ fontSize: '0.9rem', color: '#718096' }}>Total Children</div>
            </div>
            <div className="stat-card" style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div className="stat-value" style={{ fontSize: '2.5rem', fontWeight: 700, color: '#667eea', marginBottom: '0.5rem' }}>
                {dashboardData?.total_conversations || 0}
              </div>
              <div className="stat-label" style={{ fontSize: '0.9rem', color: '#718096' }}>Total Conversations</div>
            </div>
            <div className="stat-card" style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              textAlign: 'center'
            }}>
              <div className="stat-value" style={{ fontSize: '2.5rem', fontWeight: 700, color: '#667eea', marginBottom: '0.5rem' }}>
                {dashboardData?.total_messages || 0}
              </div>
              <div className="stat-label" style={{ fontSize: '0.9rem', color: '#718096' }}>Total Messages</div>
            </div>
          </div>

          <div className="children-list">
            <h2>Children Overview</h2>
            {dashboardData?.children && dashboardData.children.length > 0 ? (
              <div className="children-grid">
                {dashboardData.children.map((child) => (
                  <div key={child.id} className="child-card">
                    <div className="child-header">
                      <h3>{child.username}</h3>
                      {child.age && <span className="age-badge">Age: {child.age}</span>}
                    </div>
                    <div className="child-stats">
                      <div className="child-stat">
                        <span className="child-stat-label">Conversations:</span>
                        <span className="child-stat-value">{child.conversation_count}</span>
                      </div>
                      <div className="child-stat">
                        <span className="child-stat-label">Messages:</span>
                        <span className="child-stat-value">{child.message_count}</span>
                      </div>
                      <div className="child-stat">
                        <span className="child-stat-label">Last Activity:</span>
                        <span className="child-stat-value">{formatDate(child.last_activity)}</span>
                      </div>
                    </div>
                    {child.interests && child.interests.length > 0 && (
                      <div className="child-interests" style={{ marginTop: '1rem' }}>
                        <h4 style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Interests:</h4>
                        <div className="interests-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {child.interests.map((interest, index) => (
                            <span
                              key={index}
                              className="interest-tag"
                              onClick={() => handleInterestClick(interest, child)}
                              style={{
                                background: 'rgba(102, 126, 234, 0.1)',
                                color: '#667eea',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '12px',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = 'rgba(102, 126, 234, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                              }}
                              title="Click for suggestions"
                            >
                              {interest}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="child-actions" style={{ marginTop: '1rem' }}>
                      <button
                        className="btn-small"
                        onClick={() => loadChildConversations(child.id, child.username)}
                        disabled={loadingConversations}
                      >
                        {loadingConversations ? 'Loading...' : 'View Conversations'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No children accounts yet. Create your first child account to get started.</p>
                <button
                  className="btn"
                  onClick={onSwitchToChildren}
                >
                  Create Child Account
                </button>
              </div>
            )}
          </div>

          {/* Conversations Modal */}
          {showConversationsModal && (
            <div className="modal-overlay" onClick={() => setShowConversationsModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Conversations - {selectedChild}</h2>
                  <button
                    className="modal-close-btn"
                    onClick={() => setShowConversationsModal(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  {childConversations.length > 0 ? (
                    <div className="conversations-list">
                      {childConversations.map((conv) => (
                        <div
                          key={conv.id}
                          className="conversation-item-modal"
                          onClick={() => handleConversationClick(conv, selectedChildId)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="conversation-title">{conv.title}</div>
                          <div className="conversation-date">
                            {new Date(conv.created_at).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No conversations yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Embedded Chat */}
          {embeddedChat && (
            <div className="embedded-chat-overlay" onClick={closeEmbeddedChat}>
              <div className="embedded-chat-container" onClick={(e) => e.stopPropagation()}>
                <div className="embedded-chat-header">
                  <h3>{embeddedChat.conversation.title}</h3>
                  <button className="embedded-chat-close" onClick={closeEmbeddedChat}>
                    ×
                  </button>
                </div>
                <div className="embedded-chat-messages">
                  {loadingMessages ? (
                    <div className="loading">Loading messages...</div>
                  ) : embeddedMessages.length === 0 ? (
                    <div className="empty-state">
                      <p>No messages in this conversation.</p>
                    </div>
                  ) : (
                    embeddedMessages.map((message, index) => {
                      const childName = dashboardData?.children?.find(
                        child => child.id === embeddedChat.childId
                      )?.username || 'Child';
                      
                      return (
                        <div key={index} className={`message ${message.role}`}>
                          <div className="message-content">
                            <div className="message-role">
                              {message.role === 'user' ? childName : 'Assistant'} :
                            </div>
                            {message.role === 'assistant' ? (
                              <MessageFormatter content={message.content} />
                            ) : (
                              <div>{message.content}</div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Interest Suggestions Modal */}
          {interestModalOpen && (
            <div className="modal-overlay" onClick={() => setInterestModalOpen(false)}>
              <div className="modal-content interest-suggestions-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>
                    {selectedInterest} - {selectedChildForInterest?.username}
                  </h2>
                  <button
                    className="modal-close-btn"
                    onClick={() => setInterestModalOpen(false)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  {loadingSuggestions ? (
                    <div className="loading">Generating suggestions...</div>
                  ) : interestSuggestions ? (
                    <div className="interest-suggestions">
                      {interestSuggestions.conversation_topics && (
                        <div className="suggestion-section">
                          <h3>💬 Conversation Topics</h3>
                          <ul className="suggestion-list">
                            {interestSuggestions.conversation_topics.map((topic, idx) => (
                              <li key={idx}>{topic}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {interestSuggestions.activities && (
                        <div className="suggestion-section">
                          <h3>🎨 Activities</h3>
                          <ul className="suggestion-list">
                            {interestSuggestions.activities.map((activity, idx) => (
                              <li key={idx}>{activity}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {interestSuggestions.learning_resources && (
                        <div className="suggestion-section">
                          <h3>📚 Learning Resources</h3>
                          <ul className="suggestion-list">
                            {interestSuggestions.learning_resources.map((resource, idx) => (
                              <li key={idx}>{resource}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {interestSuggestions.fun_facts && (
                        <div className="suggestion-section">
                          <h3>✨ Fun Facts</h3>
                          <ul className="suggestion-list">
                            {interestSuggestions.fun_facts.map((fact, idx) => (
                              <li key={idx}>{fact}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No suggestions available.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardTab;

